import React from 'react';
import { Document } from '../../types/Document'; // Adjust path as needed
import { API_URL } from '@/lib/config';

interface DocumentPreviewPaneProps {
  document: Document | null;
  userToken: string | undefined | null;
  currentLayoutType: 'stacked' | 'side-by-side';
  height?: number; // Optional height prop
}

const DocumentPreviewPane: React.FC<DocumentPreviewPaneProps> = ({
  document,
  userToken,
  currentLayoutType,
  height, // Destructure height
}) => {
  if (!document) return null;

  const paneStyle = {
    height: height ? `${height}px` : (currentLayoutType === 'stacked' ? '25%' : '100%'),
    width: '100%',
  };
  
  const containerClasses = `overflow-auto ${currentLayoutType === 'stacked' ? 'w-full pb-0 border-b border-gray-200' : 'w-full md:w-1/2 h-full border-r border-gray-200'}`;

  // Base style for the main container
  let containerStyle: React.CSSProperties = {};
  if (height) {
    containerStyle.height = `${height}px`;
    containerStyle.width = '100%'; // Ensure width is maintained when height is dynamic
  } else if (currentLayoutType === 'stacked') {
    // Default height for stacked layout if not resizing
    // containerStyle.height = '25%'; // This was the old h-1/4, adjust if needed or rely on flex
  }
  // Remove bottom padding for stacked view to make resize handle flush
  if (currentLayoutType === 'stacked') {
    containerStyle.paddingBottom = '0px';
  }

  if (document.documentType === 'DSI' && document.processingResult) {
    return (
      <div 
        className={containerClasses}
        style={{ ...containerStyle, height: '50vh', overflowY: 'auto' }}
      >
        <pre>{JSON.stringify(document.processingResult, null, 2)}</pre>
      </div>
    );
  }

  return (
    <div 
      className={containerClasses}
      style={containerStyle}
    >
      {document.type === 'application/pdf' ? (
        <iframe
          src={`${API_URL}/documents/${document.id}/preview?token=${encodeURIComponent(userToken || '')}`}
          style={{ width: '100%', height: '100%' }} // Iframe should fill its container
          title="PDF Preview"
          aria-label="PDF Preview"
        />
      ) : (
        <div 
          className="flex items-center justify-center h-full bg-gray-50 p-4"
          // No specific style needed here if parent controls height and this is h-full
        >
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Preview Unavailable</h3>
            <p className="mt-1 text-sm text-gray-500">
              Preview is not available for <strong>{document.type}</strong> files.
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => {
                  if (document?.id) { 
                    const url = `${API_URL}/documents/${document.id}/content`;
                    window.open(url, '_blank');
                  }
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Download Document
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentPreviewPane; 